import { Navigate, useLocation } from 'react-router-dom';
import NotFoundPage from '../pages/NotFoundPage';

/**
 * Client-side fallback for legacy URL structures that aren't caught
 * by _redirects (Netlify) or vercel.json (Vercel). Ensures deep links
 * keep working even in local/preview environments.
 *
 * Canonical 301s are configured at the edge — this component just mirrors
 * those rules inside the SPA. Keep them in sync with /frontend/vercel.json.
 */

const ARTIST_ROOT_SLUGS = new Set([
  'ali-b', 'andy-warhol-new-works', 'barbara-hepworth', 'damien-hirst',
  'daniel-martin', 'david-hockney', 'dom-pattinson', 'james-rawson',
  'joachim-van-der-vlugt', 'joe-castro', 'joshua-flint', 'josias-figueirido',
  'julieta-schildknecht', 'kate-garner', 'kaws', 'lucie-flynn',
  'marc-chagall', 'markus-klinko', 'mason-storm', 'michael-joseph',
  'pavel-gempler', 'peter-mcardle', 'rebecca-fontaine-wolf', 'rosso-art',
  'shani-joel', 'terry-oneill', 'tracey-emin', 'christian-little',
  'james-green-2',
]);

const NEWS_ROOT_SLUGS = new Set([
  'bowie-unseen', 'bowie-by-markus-klinko', 'cocktails-with-marilyn',
  'dan-baldwin-piece-raises-250k-for-the-bhf', 'david-hockneys-birthday',
  'dom-pattinson-art-exhibition', 'dom-pattinson-christmas-art-hunt',
  'endangered', 'full-circle', 'helmut-newton-100-years',
  'hyde-park-festival', 'i-see-wonderful-things', 'kate-moss-by-kate-garner',
  'mason-storm-print-release', 'sir-peter-blake-wooden-puzzle-series',
  'stunning-dan-baldwin-vases', 'the-daughters-of-medusa',
  'the-rolling-stones-unseen-pictures', 'tim-muddiman-new-wallpaper-collection',
  'unseen-rolling-stones', 'valentine-treasure-hunt', 'warrior-women',
  'zebra-one-gallery-celebrates-its-40th-birthday',
  'zebra-one-gallery-on-london-buses',
  'zebra-one-gallery-on-the-factoryline', 'dain-nyc-collaboration-news',
]);

const EXHIBITION_ROOT_SLUGS = new Set([
  'art-in-mind-exhibition', 'with-art-in-mind', 'with-art-in-mind-exhibition',
  'kate-moss-at-the-savoy', 'orbit-art-show', 'pieces-of-a-woman-exhibition',
  'ten-starts-one-exhibition', 'terry-oneill-exhibition',
  'viewing-room', 'virtual-exhibition-at-zebra-one-gallery',
]);

const PREFIX_RULES = [
  { prefix: '/product/', to: '/shop' },
  { prefix: '/product-category/', to: '/shop' },
  { prefix: '/product-tag/', to: '/shop' },
  { prefix: '/tag/', to: '/news' },
  { prefix: '/category/', to: '/news' },
  { prefix: '/author/', to: '/news' },
  { prefix: '/feed/', to: '/news' },
  { prefix: '/2013/', to: '/news' },
  { prefix: '/2017/', to: '/news' },
  { prefix: '/2018/', to: '/news' },
  { prefix: '/2020/', to: '/news' },
  { prefix: '/2021/', to: '/news' },
  { prefix: '/wp-admin', to: '/admin/login' },
  { prefix: '/wp-content/', to: '/' },
  { prefix: '/wp-includes/', to: '/' },
  { prefix: '/wp-json/', to: '/' },
  { prefix: '/ham-high-', to: '/news' },
];

const EXACT_RULES = {
  '/cart': '/shop',
  '/checkout-page': '/shop',
  '/my-account': '/shop',
  '/wishlist': '/shop',
  '/enquiry-cart-page': '/contact',
  '/store': '/shop',
  '/all-artists': '/artists',
  '/blog': '/news',
  '/articles': '/news',
  '/press': '/news',
  '/press-links': '/news',
  '/feed': '/news',
  '/mailing-list': '/contact',
  '/privacy-policy': '/about',
  '/terms-conditions': '/about',
  '/identity': '/about',
  '/zebra-one-gallery-archives': '/about',
  '/search': '/shop',
  '/art-interiors': '/art-consultation',
  '/augmented-reality-art-app': '/art-consultation',
  '/augmented-reality-art-gallery-app': '/art-consultation',
  '/xmlrpc.php': '/',
};

export const LegacyCatchAll = () => {
  const location = useLocation();
  const path = location.pathname.toLowerCase();
  const slug = path.slice(1);

  if (EXACT_RULES[path]) {
    return <Navigate to={EXACT_RULES[path]} replace />;
  }
  for (const rule of PREFIX_RULES) {
    if (path.startsWith(rule.prefix)) return <Navigate to={rule.to} replace />;
  }
  if (ARTIST_ROOT_SLUGS.has(slug)) return <Navigate to="/artists" replace />;
  if (NEWS_ROOT_SLUGS.has(slug)) return <Navigate to="/news" replace />;
  if (EXHIBITION_ROOT_SLUGS.has(slug)) return <Navigate to="/exhibitions" replace />;

  // Default 404 — proper Not Found page (no silent home redirect)
  return <NotFoundPage />;
};
